<template>
	<div class="main" :class="[logoStyleInfo?.position]">
		<div class="logo">
			<router-link :to="`/${serviceCode}`">
				<img
					v-if="logoImagePath"
					:src="logoImagePath"
					alt="logo"
					:style="`width: ${logoStyleInfo?.width}`"
				/>
			</router-link>
		</div>
		<div class="button_area">
			<template v-for="(button, idx) in buttonList" :key="idx">
				<MainButton v-if="entrance[button.display]" :buttonInfo="button" />
			</template>
			<div class="dscr">
				<p v-html="entranceText"></p>
			</div>
			<div class="sponsor" v-if="sponsorStyleInfo?.imagePath">
				<img
					:src="sponsorStyleInfo?.imagePath"
					alt="sponsor-image"
					:style="`width: ${sponsorStyleInfo?.width}`"
				/>
			</div>
		</div>
		<img
			v-if="wizdomeLogoPath"
			class="wizdome"
			:class="[serviceStyle?.wizdomeLogo?.position]"
			:src="wizdomeLogoPath"
			alt="wizdome-logo"
		/>

		<!-- 챗봇 -->
		<twc-button v-if="isHasTwc" @onClickChatbot="onClickChatbot" />
		<gitple-button v-else-if="isHasGitple" />

		<!-- TWC 챗봇 정보입력 팝업 -->
		<twc-popup ref="twcPopup" />

		<!-- 에러 팝업 -->
		<BaseErrorPopup ref="errorPopup" />
	</div>
</template>

<script>
import { ref, computed, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import MainButton from '@/components/Main/MainButton.vue';
import GitpleButton from '@/components/GitpleButton.vue';
import useTheme from '@/composables/useTheme';
import { serviceMappers } from '@/model/mappers.js';
import BaseErrorPopup from '@/components/Base/BaseErrorPopup.vue';
import TwcButton from '@/components/TwcButton.vue';
import TwcPopup from '@/components/TwcPopup.vue';

export default {
	name: 'Main',
	components: { MainButton, BaseErrorPopup, TwcButton, GitpleButton, TwcPopup },
	setup() {
		const route = useRoute();
		const store = useStore();
		const serviceInfo = computed(() => store.state.common.serviceInfo);
		const serviceCode = route.params.serviceCode;
		const entrance = computed(() => store.getters['common/entrance']);
		// 노선정보 화면 보여주는지 여부
		const timeFareDisplayYn = computed(
			() => store.getters['common/timeFareDisplayYn'],
		);
		const bookSteps = computed(() => store.state.book.bookSteps);
		const etcBookSteps = computed(() => store.state.book.etcBookSteps);
		const wizdomeLogoPath = ref();

		const twcPopup = ref();
		const errorPopup = ref(null);

		const buttonList = computed(() => [
			{
				name: '예약하기',
				page: timeFareDisplayYn.value ? 'LineInfo' : bookSteps.value[0]?.page,
				display: 'bookableYn',
				class: 'book',
			},
			{
				name: '기타노선신청',
				page: etcBookSteps.value[0]?.page,
				display: 'etcYn',
				class: 'etc',
			},
			{
				name: '예약확인',
				page: 'BookConfirm',
				display: 'myBookYn',
				class: 'mybook',
			},
			{
				name: '예약취소',
				page: 'BookCancel',
				display: 'cancelableYn',
				class: 'cancel',
			},
			{
				name: '주의사항',
				page: 'FAQ',
				display: 'faqYn',
				class: entrance.value.cancelableYn ? 'faq' : 'faq full-btn',
			},
		]);

		const entranceText = computed(() => {
			/**
			 * entranceText 값 문자열에서 배열로 타입이 수정됨
			 * 현재 운영중인 서비스는 텍스트로 들어올 수 있어 배열 또는 텍스트로 들어오는 경우를 모두 고려해야 함
			 */
			const entranceText = store.getters['common/entranceText'];
			if (Array.isArray(entranceText)) {
				return entranceText.join('<br />');
			}

			return store.getters['common/entranceText'];
		});

		const serviceStyle = ref({});
		const setServiceStyle = style => {
			serviceStyle.value = style || serviceMappers[serviceCode.toUpperCase()];
			if (!serviceStyle.value) {
				return;
			}
			wizdomeLogoPath.value = serviceStyle.value?.wizdomeLogo?.path
				? serviceStyle.value?.wizdomeLogo?.path
				: require('@/assets/img/wizdome.png');
			useTheme(serviceStyle.value);
		};

		const getServiceInfo = async () => {
			const result = await store.dispatch('common/FETCH_SERVICE_INFO', {
				serviceCode: serviceCode.toUpperCase(),
			});

			if (result.resultCode !== 0) {
				errorPopup.value.openErrorPopup({
					errorCode: result.resultCode,
					errorTime: result.resultTs,
					errorMessage: result.resultMessage,
				});
				return;
			}

			const style = result.data?.contentProps?.style;
			setServiceStyle(style);
		};

		const logoStyleInfo = computed(() => serviceStyle.value?.logo);
		const logoImagePath = computed(
			() => logoStyleInfo.value?.imagePath ?? serviceStyle.value.logoPath,
		);
		const sponsorStyleInfo = computed(() => serviceStyle.value?.sponsor);

		// 예약정보 삭제
		store.dispatch('book/CLEAR_LINES');
		// accessToken 삭제
		store.dispatch('member/CLEAR_ACCESS_TOKEN');
		// 예약자 정보 삭제
		store.dispatch('member/CLEAR_USER_INFO');
		if (window.sessionStorage.getItem('payInfo')) {
			window.sessionStorage.removeItem('payInfo');
		}

		/** 챗봇 처리 */
		const isHasTwc = computed(() => !!store.getters['common/getTwcConfig']);
		const isHasGitple = computed(
			() => !!store.getters['common/getGitpleConfig'],
		);

		const onClickChatbot = () => {
			twcPopup.value?.openTwcPopup();
		};

		onBeforeMount(() => {
			const style = serviceInfo.value?.contentProps?.style;
			Object.keys(serviceInfo.value).length
				? setServiceStyle(style)
				: getServiceInfo();
		});

		return {
			twcPopup,
			errorPopup,
			entrance,
			entranceText,
			buttonList,
			serviceCode,
			serviceStyle,
			wizdomeLogoPath,
			isHasTwc,
			isHasGitple,
			logoStyleInfo,
			logoImagePath,
			sponsorStyleInfo,
			onClickChatbot,
		};
	},
};
</script>
